import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useEffect, useState } from 'react'
import { optionalEmailSchema } from '@/lib/validations'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'
import { adSupplierGroupService } from '@/services/ad_supplier_group_service'
import { useAuth } from '@/hooks/useAuth'
import { useViewMode } from '@/stores/viewMode'
import { useAdSupplierGroupSuppliers } from '@/hooks/useAdSupplierGroups'
import { toast } from 'sonner'
import { AdSupplierGroup } from '@magnetar/admin-api-interfaces'
import { Plus, Building2, Trash2 } from 'lucide-react'
import { AssignAdSupplierDialog } from './AssignAdSupplierDialog'

const formSchema = z.object({
  group_name: z.string().min(1, { message: '請輸入集團名稱' }),
  group_code: z.string().optional(),
  manager_name: z.string().min(1, { message: '請輸入負責人姓名' }),
  phone: z.string().min(1, { message: '請輸入聯絡電話' }),
  email: optionalEmailSchema(),
  address: z.string().optional(),
  description: z.string().optional(),
  is_active: z.boolean(),
})

interface AdSupplierGroupDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
  adSupplierGroup?: AdSupplierGroup
}

export function AdSupplierGroupDialog({
  open,
  onOpenChange,
  onSuccess,
  adSupplierGroup,
}: AdSupplierGroupDialogProps) {
  const { token } = useAuth()
  const { id: providerId } = useViewMode()
  const isEditMode = !!adSupplierGroup

  // Assign supplier dialog state
  const [assignDialogOpen, setAssignDialogOpen] = useState(false)

  // Remove supplier confirmation state
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false)
  const [removingSupplier, setRemovingSupplier] = useState<{
    supplier_id: number
    supplier_name: string
  } | null>(null)

  // Get assigned suppliers (only in edit mode)
  const {
    suppliers: assignedSuppliers,
    isLoading: suppliersLoading,
    mutate: mutateSuppliers,
  } = useAdSupplierGroupSuppliers(
    isEditMode && open ? adSupplierGroup?.ad_supplier_group_id ?? null : null,
  )

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      group_name: '',
      group_code: '',
      manager_name: '',
      phone: '',
      email: '',
      address: '',
      description: '',
      is_active: true,
    },
  })

  useEffect(() => {
    if (isEditMode && adSupplierGroup) {
      form.reset({
        group_name: adSupplierGroup.group_name,
        group_code: adSupplierGroup.group_code || '',
        manager_name: adSupplierGroup.manager_name,
        phone: adSupplierGroup.phone,
        email: adSupplierGroup.email || '',
        address: adSupplierGroup.address || '',
        description: adSupplierGroup.description || '',
        is_active: adSupplierGroup.is_active,
      })
    } else {
      form.reset({
        group_name: '',
        group_code: '',
        manager_name: '',
        phone: '',
        email: '',
        address: '',
        description: '',
        is_active: true,
      })
    }
  }, [isEditMode, adSupplierGroup, form, open])

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    if (!token) return

    const data = {
      group_name: values.group_name,
      group_code: values.group_code || null,
      manager_name: values.manager_name,
      phone: values.phone,
      email: values.email || null,
      address: values.address || null,
      description: values.description || null,
      is_active: values.is_active,
      provider_id: providerId,
    }

    if (isEditMode && adSupplierGroup) {
      const result = await adSupplierGroupService.updateAdSupplierGroup(
        adSupplierGroup.ad_supplier_group_id,
        data,
        token,
      )

      if (result.success) {
        toast.success('更新投資方集團成功')
        form.reset()
        onOpenChange(false)
        onSuccess()
      } else {
        toast.error((result as { error?: { message?: string } }).error?.message || '更新投資方集團失敗')
      }
    } else {
      const result = await adSupplierGroupService.createAdSupplierGroup(
        {
          ...data,
          provider_id: providerId,
        },
        token,
      )

      if (result.success) {
        toast.success('新增投資方集團成功')
        form.reset()
        onOpenChange(false)
        onSuccess()
      } else {
        toast.error((result as { error?: { message?: string } }).error?.message || '新增投資方集團失敗')
      }
    }
  }

  const handleRemoveSupplier = (supplier: { supplier_id: number; supplier_name: string }) => {
    setRemovingSupplier(supplier)
    setRemoveDialogOpen(true)
  }

  const confirmRemoveSupplier = async () => {
    if (!token || !removingSupplier || !adSupplierGroup) return

    const result = await adSupplierGroupService.removeSupplier(
      adSupplierGroup.ad_supplier_group_id,
      removingSupplier.supplier_id,
      token,
    )

    if (result.success) {
      toast.success(`已將「${removingSupplier.supplier_name}」從集團移除`)
      mutateSuppliers()
    } else {
      toast.error('移除投資方失敗')
    }

    setRemoveDialogOpen(false)
    setRemovingSupplier(null)
  }

  const handleAssignSuccess = () => {
    mutateSuppliers()
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isEditMode ? '編輯投資方集團' : '新增投資方集團'}</DialogTitle>
            <DialogDescription>
              {isEditMode ? '更新投資方集團資訊' : '建立新的投資方集團'}
            </DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="group_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>集團名稱 *</FormLabel>
                    <FormControl>
                      <Input placeholder="請輸入集團名稱" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="group_code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>集團代碼</FormLabel>
                    <FormControl>
                      <Input placeholder="請輸入集團代碼" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="manager_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>負責人 *</FormLabel>
                    <FormControl>
                      <Input placeholder="請輸入負責人姓名" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>電話 *</FormLabel>
                    <FormControl>
                      <Input placeholder="請輸入聯絡電話" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>電子郵件</FormLabel>
                    <FormControl>
                      <Input placeholder="請輸入電子郵件" type="email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>地址</FormLabel>
                    <FormControl>
                      <Input placeholder="請輸入地址" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>描述</FormLabel>
                    <FormControl>
                      <Textarea placeholder="請輸入集團描述" className="min-h-[80px]" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="is_active"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                    <div className="space-y-0.5">
                      <FormLabel>啟用狀態</FormLabel>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />

              {/* Assigned Suppliers Section - Only show in edit mode */}
              {isEditMode && (
                <>
                  <Separator className="my-4" />
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-medium">已指派投資方</h4>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setAssignDialogOpen(true)}
                      >
                        <Plus className="mr-1 h-4 w-4" />
                        指派投資方
                      </Button>
                    </div>

                    <div className="rounded-md border">
                      {suppliersLoading ? (
                        <div className="p-4 space-y-2">
                          <Skeleton className="h-4 w-full" />
                          <Skeleton className="h-4 w-full" />
                        </div>
                      ) : assignedSuppliers && assignedSuppliers.length > 0 ? (
                        <div className="divide-y max-h-[200px] overflow-y-auto">
                          {assignedSuppliers.map((supplier) => (
                            <div
                              key={supplier.supplier_id}
                              className="flex items-center justify-between p-3 hover:bg-muted/50"
                            >
                              <div className="flex items-center gap-2">
                                <Building2 className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm">{supplier.supplier_name}</span>
                                {!supplier.is_active && (
                                  <span className="text-xs text-muted-foreground">(已停用)</span>
                                )}
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                                onClick={() => handleRemoveSupplier(supplier)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="p-4 text-center text-sm text-muted-foreground">
                          尚未指派任何投資方
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      共 {assignedSuppliers?.length ?? 0} 間投資方
                    </p>
                  </div>
                </>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  取消
                </Button>
                <Button type="submit">{isEditMode ? '更新' : '新增'}</Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Assign Supplier Dialog */}
      {isEditMode && adSupplierGroup && (
        <AssignAdSupplierDialog
          open={assignDialogOpen}
          onOpenChange={setAssignDialogOpen}
          groupId={adSupplierGroup.ad_supplier_group_id}
          onSuccess={handleAssignSuccess}
        />
      )}

      {/* Remove Supplier Confirmation Dialog */}
      <AlertDialog open={removeDialogOpen} onOpenChange={setRemoveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>確定要移除嗎？</AlertDialogTitle>
            <AlertDialogDescription>
              此操作將把「{removingSupplier?.supplier_name}
              」從集團移除。投資方本身不會被刪除，之後可以重新指派。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRemoveSupplier}>確定移除</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
